// AppShellHost — the lifecycle-surface handlers (master audit R5, C1 #9).
//
// Extracted verbatim from AppShellHost.tsx: the shared rename / delete+undo /
// duplicate flows behind every rail row's and object view's overflow, the
// source summary/notes commands, the export packaging + citation-gate flow, the
// shared overflow-menu builders. Every write goes through the ports; the
// library/conversation lists reload after so the rail never shows a lie.
//
// It calls ONE React hook — `useLingui()` — for the overflow-menu labels (the
// Lingui `t` macro must be resolved in-file; the CoachHost host-hook does the
// same). So this call site adds a single, stable, unconditional hook to the
// component's order, at the position AppShellHost previously held these
// handlers. It threads the shell state + setters the handlers read/write.
//
// One collaborator is declared LOWER in AppShellHost (the History overlay
// opener `openHistoryForResource`): it arrives as a deferred arrow, fired only
// on a later event, so referencing it is temporally safe — the same
// forward-declared-handler pattern useObjectNav uses.

import type * as Core from "@bilda/core";
import { exhaustSwitchCase, Result } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import type { Dispatch, SetStateAction } from "react";
import type { ExportState } from "../../components/ExportFlow/ExportFlow.js";
import type { MenuItem } from "../../components/Menu/Menu.js";
import type {
    ConversationId,
    ConversationRailItem,
    RailCategory,
    ResourceId,
    ResourceRef,
} from "../../components/Navigation/types.js";
import { railCategoryForResourceKind } from "../../components/Navigation/types.js";
import type { MaterialQuality } from "../../components/QualitySummary/QualitySummary.js";
import type { RecentThread } from "../../components/WorkspaceLanding/WorkspaceLanding.js";
import {
    markArchived,
    retitleConversation,
    titleForConversation,
} from "../conversationSummary.js";
import { externalErrorId } from "../errorId.js";
import { exportNextStep } from "../exportFailure.js";
import { type ExternalError, toDisplay } from "../externalError.js";
import type { FrameTransition, HostNotice, SaveRetry } from "../frame.js";
import type { AppPorts, ExportReady, Library, PortResult } from "../ports.js";
import { extForFormat } from "./constants.js";
import type {
    PromoteFileOutcome,
    PromoteFileToSourceIntent,
} from "./resourceDetailModel.js";

// What is currently being renamed inline in the rail, if anything. A
// discriminated union so each inline-rename target is a typed variant (never
// a boolean): a resource row (any kind) or a conversation row — the two land
// in different id spaces and dispatch different rename commands.
export type Renaming =
    | { readonly type: "resource"; readonly ref: ResourceRef }
    | {
          readonly type: "conversation";
          readonly conversationId: string;
      };

// What a successful re-upload leads to. `conversationId`: it was started from
// a chat whose turn stopped on the missing content — the restore notice
// offers "Continue" there. `onRestored`: the calling view re-reads itself
// (e.g. the library detail). Both absent ⇒ just the notice + source reload.
type ReuploadFollowUp = {
    readonly conversationId?: string;
    readonly onRestored?: () => void;
};

export type LifecycleSurfacesDeps = {
    readonly ports: AppPorts;
    readonly setLibrary: Dispatch<SetStateAction<Library | null>>;
    readonly dispatch: Dispatch<FrameTransition>;
    readonly showNotice: (n: HostNotice) => void;
    readonly displayText: (e: ExternalError) => string;
    readonly selectCategory: (category: RailCategory) => void;
    readonly setRenaming: Dispatch<SetStateAction<Renaming | null>>;
    readonly conversationSummaries: readonly RecentThread[];
    // Patch the held list optimistically / re-read it from the server (G6:
    // the list is a `Remote`, owned by the shell foundation).
    readonly updateConversationSummaries: (
        f: (prev: readonly RecentThread[]) => readonly RecentThread[],
    ) => void;
    readonly retryConversationList: () => void;
    readonly setConfirmDelete: Dispatch<SetStateAction<ResourceRef | null>>;
    readonly setConfirmDeleteConversation: Dispatch<
        SetStateAction<ConversationRailItem | null>
    >;
    readonly setSourceReloadKey: Dispatch<SetStateAction<number>>;
    readonly setExportState: Dispatch<SetStateAction<ExportState>>;
    readonly setExportReady: Dispatch<SetStateAction<ExportReady | null>>;
    readonly mkConversationId: () => ConversationId;
    readonly refFor: (id: ResourceId) => ResourceRef | undefined;
    readonly openRef: (ref: ResourceRef) => void;
    readonly activeSourceId: string | null;
    readonly activeMaterialId: string | null;
    // The material the export sheet is open for (null: the sheet is closed).
    readonly exportMaterialId: string | null;
    readonly quality: MaterialQuality | null;
    readonly policy: Core.Policy.Evaluate.PolicyEvaluation | null;
    // Forward-declared collaborators (see header): each fires only on a later
    // event, so the deferred arrow / thunk is temporally safe.
    readonly openHistoryForResource: (resourceKey: string) => void;
};

export type LifecycleSurfaces = {
    readonly submitResourceRename: (id: ResourceId, title: string) => void;
    readonly conversationTitleFor: (id: string) => string;
    readonly submitConversationRename: (id: string, title: string) => void;
    readonly deleteResourceNow: (ref: ResourceRef) => void;
    readonly undoDelete: (ref: ResourceRef, priorContentHash: string) => void;
    readonly archiveResourceNow: (ref: ResourceRef) => void;
    readonly unarchiveResourceNow: (ref: ResourceRef) => void;
    readonly promoteFileNow: (
        intent: PromoteFileToSourceIntent,
    ) => Promise<PromoteFileOutcome>;
    readonly deleteConversationNow: (item: ConversationRailItem) => void;
    readonly undoDeleteConversation: (conversationId: string) => void;
    readonly saveSourceSummaryNow: (text: string) => void;
    readonly addSourceNote: (blockId: string | undefined, text: string) => void;
    readonly deleteSourceNote: (annotationId: string) => void;
    // F9/F15: opens a single-file picker targeting ONE existing Source and
    // restores its missing stored content on a byte-identical match — INTO
    // that same source, so references, conversations and citations keep
    // working. Reached from the failed read step, the `request_upload`
    // action-offer move, and the source's missing-content state.
    // `followUp` says what the restore confirmation may offer next.
    readonly requestUploadForSource: (
        sourceId: string,
        sourceTitle: string | undefined,
        followUp?: ReuploadFollowUp,
    ) => void;
    readonly downloadExportById: (exportId: ResourceId) => void;
    readonly resourceOverflowItems: (
        ref: ResourceRef,
        archived?: boolean,
    ) => readonly MenuItem[];
    readonly beginRenameConversation: (conversationId: string) => void;
    // Set a conversation aside; the message offers Undo.
    readonly archiveConversationNow: (conversationId: string) => void;
    readonly unarchiveConversationNow: (conversationId: string) => void;
    // A failed save's Retry (the "save-failed" message).
    readonly retrySave: (retry: SaveRetry) => void;
    readonly packageNow: (formatId: string) => void;
    readonly onExportStart: (formatId: string) => void;
};

export const useLifecycleSurfaces = ({
    ports,
    setLibrary,
    dispatch,
    showNotice,
    displayText,
    selectCategory,
    setRenaming,
    conversationSummaries,
    updateConversationSummaries,
    retryConversationList,
    setConfirmDelete,
    setConfirmDeleteConversation,
    setSourceReloadKey,
    setExportState,
    setExportReady,
    mkConversationId,
    refFor,
    openRef,
    activeSourceId,
    activeMaterialId,
    exportMaterialId,
    quality,
    policy,
    openHistoryForResource,
}: LifecycleSurfacesDeps): LifecycleSurfaces => {
    const { t } = useLingui();
    // Re-read the workspace library (post-mutation truth for the rail).
    const reloadLibrary = async (): Promise<void> => {
        const lib = await ports.loadLibrary();
        if (Result.isOk(lib)) setLibrary(lib.value);
    };

    // A rename went through: the remembered tab/recency refs carry titles, so
    // patch them everywhere the id appears (the library itself reloads).
    const retitleRef = (id: ResourceId, title: string) =>
        dispatch({ type: "retitle-resource", id, title });

    // Start an inline rename — the rail row flips into the RowRename field,
    // and the rail switches to the resource's owning category so the field
    // is actually visible when the rename was summoned from an object
    // view's overflow (elsewhere than the rail itself).
    const beginRenameResource = (ref: ResourceRef) => {
        selectCategory(railCategoryForResourceKind(ref.type));
        setRenaming({ type: "resource", ref });
    };
    const submitResourceRename = (id: ResourceId, title: string) => {
        setRenaming(null);
        const run = async () => {
            const r = await ports.renameResource(id, title);
            if (Result.isErr(r)) {
                showNotice({
                    type: "lifecycle-failed",
                    message: displayText(r.error),
                });
                return;
            }
            retitleRef(id, title);
            await reloadLibrary();
        };
        void run();
    };

    // Re-read the conversation summaries (post-mutation truth for the rail +
    // tab label) through the ONE list read (useLibraryLoad), which keeps the
    // held rows visible while it runs (stale-while-revalidate).
    const reloadConversations = retryConversationList;

    // The tab-strip label for a conversation tab — its summary title (rename
    // projects here immediately, since the rail + tabs both read this one
    // source), falling back to the generic Assistant label before the summary
    // has loaded / for a just-created thread.
    const conversationTitleFor = (id: string): string =>
        titleForConversation(conversationSummaries, id, t`New conversation`);

    // Start an inline rename of a conversation row — switch the rail to the
    // Conversations category so the RowRename field is visible (mirrors
    // `beginRenameResource`).
    const beginRenameConversation = (conversationId: string) => {
        selectCategory("conversations");
        setRenaming({ type: "conversation", conversationId });
    };
    // Retitle a conversation through the port. Optimistic: patch the summary
    // title in place (the rail + tab label both project from it — everything-
    // must-feel-fast), then reconcile against the server's truth.
    const submitConversationRename = (id: string, title: string) => {
        setRenaming(null);
        updateConversationSummaries((prev) =>
            retitleConversation(prev, id, title),
        );
        const run = async () => {
            const r = await ports.renameConversation(id, title);
            if (Result.isErr(r))
                showNotice({
                    type: "save-failed",
                    retry: { type: "rename-conversation", id, title },
                    message: displayText(r.error),
                });
            reloadConversations();
        };
        void run();
    };

    // Confirmed delete: soft-delete through the port, close the resource's
    // tab if it is open (force-close — the resource is gone; there is no
    // buffer to guard), refresh the rail, then offer undo. The pre-delete
    // hash rides in the notice so undo restores exactly what the author saw.
    const deleteResourceNow = (ref: ResourceRef) => {
        setConfirmDelete(null);
        const run = async () => {
            const r = await ports.deleteResource(ref.id);
            if (Result.isErr(r)) {
                showNotice({
                    type: "lifecycle-failed",
                    message: displayText(r.error),
                });
                return;
            }
            dispatch({
                type: "drop-resource-tab",
                id: ref.id,
                freshConversationId: mkConversationId(),
            });
            await reloadLibrary();
            showNotice({
                type: "resource-deleted",
                ref,
                priorContentHash: r.value.contentHash,
            });
        };
        void run();
    };

    // Duplicate a material: run the ONE `duplicateResource` command, refresh
    // the library so the copy exists in the rail, then land the author in the
    // editor on it (the copy is a fresh working draft, ready to rework). A
    // refusal surfaces as a lifecycle notice.
    const duplicateResourceNow = (ref: ResourceRef) => {
        const run = async () => {
            const r = await ports.duplicateResource(ref.id);
            if (Result.isErr(r)) {
                showNotice({
                    type: "lifecycle-failed",
                    message: displayText(r.error),
                });
                return;
            }
            await reloadLibrary();
            openRef({
                type: "material",
                id: r.value.materialId,
                title: ref.title,
            });
        };
        void run();
    };

    // The undo toast's action: restore through the event log's pre-image.
    const undoDelete = (ref: ResourceRef, priorContentHash: string) => {
        const run = async () => {
            const r = await ports.restoreResource(ref.id, priorContentHash);
            if (Result.isErr(r)) {
                showNotice({
                    type: "lifecycle-failed",
                    message: displayText(r.error),
                });
                return;
            }
            await reloadLibrary();
        };
        void run();
    };

    // Archive (set aside): run the `archiveResource` command, then refresh
    // the rail so the row drops out of the default buckets into "Archived"
    // (F86). The resource still exists and its tab stays open — archive is a
    // set-aside overlay, not a removal. A refusal surfaces as a notice.
    const archiveResourceNow = (ref: ResourceRef) => {
        const run = async () => {
            const r = await ports.archiveResource(ref.id);
            if (Result.isErr(r)) {
                showNotice({
                    type: "lifecycle-failed",
                    message: displayText(r.error),
                });
                return;
            }
            await reloadLibrary();
        };
        void run();
    };

    // Unarchive: run the `unarchiveResource` command, then refresh the rail
    // so the row returns to its default bucket. Distinct command from restore.
    const unarchiveResourceNow = (ref: ResourceRef) => {
        const run = async () => {
            const r = await ports.unarchiveResource(ref.id);
            if (Result.isErr(r)) {
                showNotice({
                    type: "lifecycle-failed",
                    message: displayText(r.error),
                });
                return;
            }
            await reloadLibrary();
        };
        void run();
    };

    // Make a file a source (the library's file detail form): run the
    // promotion, refresh the library, then open the NEW source in the
    // Knowledge bank. The outcome settles the form; a refusal stays in the
    // form, not a notice.
    const promoteFileNow = async (
        intent: PromoteFileToSourceIntent,
    ): Promise<PromoteFileOutcome> => {
        const r = await ports.promoteFileToSource(
            intent.fileId,
            intent.title,
            intent.annotation,
        );
        if (Result.isErr(r))
            return { type: "failed", message: displayText(r.error) };
        await reloadLibrary();
        dispatch({ type: "open-knowledge", selectedId: r.value.sourceId });
        return { type: "promoted" };
    };

    // Confirmed conversation delete: soft-delete through the port, close the
    // conversation's tab (force — the thread is gone; a fresh id reseats the
    // working set if this empties it), refresh the rail, then offer undo. No
    // pre-delete hash rides along (a conversation carries none); the notice
    // keys undo on the id + title alone.
    const deleteConversationNow = (item: ConversationRailItem) => {
        setConfirmDeleteConversation(null);
        const run = async () => {
            const r = await ports.deleteConversation(item.id);
            if (Result.isErr(r)) {
                showNotice({
                    type: "lifecycle-failed",
                    message: displayText(r.error),
                });
                return;
            }
            dispatch({
                type: "close-conversation-tab",
                conversationId: item.id,
                freshConversationId: mkConversationId(),
            });
            reloadConversations();
            showNotice({
                type: "conversation-deleted",
                conversationId: item.id,
                title: item.title,
            });
        };
        void run();
    };

    // The conversation undo toast's action: revive the tombstoned thread.
    const undoDeleteConversation = (conversationId: string) => {
        const run = async () => {
            const r = await ports.restoreConversation(conversationId);
            if (Result.isErr(r)) {
                showNotice({
                    type: "lifecycle-failed",
                    message: displayText(r.error),
                });
                return;
            }
            reloadConversations();
        };
        void run();
    };

    // Archive / unarchive a conversation: two distinct commands, one per
    // intent (the menu offers only the verb valid for the row). Optimistic
    // like rename: the row moves between the default list and the Archived
    // section at once, then reconciles against the server's truth. The
    // thread's tab stays open — archive is a set-aside, not a removal. A
    // done archive offers Undo; a failed one puts the row back and offers
    // Retry.
    const archiveConversationNow = (id: string) => {
        const title = conversationTitleFor(id);
        runArchiveCommand(
            id,
            true,
            () => ports.archiveConversation(id),
            { type: "conversation-archived", conversationId: id, title },
            { type: "archive-conversation", id },
        );
    };
    const unarchiveConversationNow = (id: string) =>
        runArchiveCommand(
            id,
            false,
            () => ports.unarchiveConversation(id),
            null,
            { type: "unarchive-conversation", id },
        );
    const runArchiveCommand = (
        id: string,
        optimisticArchived: boolean,
        op: () => PortResult<void>,
        done: HostNotice | null,
        retry: SaveRetry,
    ) => {
        updateConversationSummaries((prev) =>
            markArchived(prev, id, optimisticArchived),
        );
        const run = async () => {
            const r = await op();
            if (Result.isErr(r))
                showNotice({
                    type: "save-failed",
                    retry,
                    message: displayText(r.error),
                });
            else if (done !== null) showNotice(done);
            reloadConversations();
        };
        void run();
    };
    const retrySave = (retry: SaveRetry) => {
        switch (retry.type) {
            case "rename-conversation":
                return submitConversationRename(retry.id, retry.title);
            case "archive-conversation":
                return archiveConversationNow(retry.id);
            case "unarchive-conversation":
                return unarchiveConversationNow(retry.id);
            default:
                return exhaustSwitchCase(retry);
        }
    };

    // Summary + notes on the active source (P10): thin command dispatches;
    // each success bumps the reload key so the source view re-reads (no
    // client event-fold yet). Failures land in the shared lifecycle notice.
    const runSourceCommand = (op: () => PortResult<void>) => {
        const run = async () => {
            const r = await op();
            if (Result.isErr(r)) {
                showNotice({
                    type: "lifecycle-failed",
                    message: displayText(r.error),
                });
                return;
            }
            setSourceReloadKey((k) => k + 1);
        };
        void run();
    };
    const saveSourceSummaryNow = (text: string) => {
        if (activeSourceId === null) return;
        const sourceId = activeSourceId;
        runSourceCommand(() => ports.saveSourceSummary(sourceId, text));
    };
    const addSourceNote = (blockId: string | undefined, text: string) => {
        if (activeSourceId === null) return;
        const sourceId = activeSourceId;
        runSourceCommand(() =>
            ports.addSourceAnnotation(sourceId, blockId, text),
        );
    };
    const deleteSourceNote = (annotationId: string) => {
        runSourceCommand(() => ports.deleteSourceAnnotation(annotationId));
    };

    // F9/F15: a single-file picker targeting ONE existing Source — the
    // `request_upload` action-offer move's handler. Wired to the SAME
    // `reuploadSourceContent` HTTP round-trip a manual re-upload would use
    // (never a new upload path); the picker element is created untethered
    // (never attached to the DOM) purely to summon the browser's native file
    // dialog, mirroring IngestHost's own hidden `<input>` idiom for a
    // single, targeted file rather than a batch. A byte mismatch surfaces as
    // the usual lifecycle-failed notice; a match bumps the source reload key
    // so the open SourceView self-heals, and a quiet "restored" notice
    // confirms it (offering "Continue" when a chat turn was waiting on it).
    const requestUploadForSource = (
        sourceId: string,
        sourceTitle: string | undefined,
        followUp?: ReuploadFollowUp,
    ) => {
        const input = document.createElement("input");
        input.type = "file";
        input.onchange = () => {
            const file = input.files?.[0];
            if (file === undefined) return;
            const run = async () => {
                const r = await ports.reuploadSourceContent(sourceId, {
                    file,
                });
                if (Result.isErr(r)) {
                    showNotice({
                        type: "lifecycle-failed",
                        message: displayText(r.error),
                    });
                    return;
                }
                setSourceReloadKey((k) => k + 1);
                showNotice({
                    type: "source-restored",
                    sourceTitle,
                    conversationId: followUp?.conversationId,
                });
                followUp?.onRestored?.();
            };
            void run();
        };
        input.click();
    };

    // "Download again" on a package row/view: re-fetch the package's download
    // URL and hand it to the browser (the same `/exports/:id` the export flow
    // used).
    const downloadExportAgain = (ref: ResourceRef) => {
        const run = async () => {
            const r = await ports.loadExport(ref.id);
            if (Result.isErr(r)) {
                showNotice({
                    type: "lifecycle-failed",
                    message: displayText(r.error),
                });
                return;
            }
            const url = r.value.downloadUrl;
            if (url !== undefined) {
                ports.downloadExport(url);
                showNotice({ type: "exported", filename: ref.title });
            }
        };
        void run();
    };

    // LANE 2 export card: re-download an export by id from the chat card (its
    // Öppna opens the right panel through the chat intent).
    const downloadExportById = (exportId: ResourceId) => {
        const ref = refFor(exportId);
        if (ref !== undefined) downloadExportAgain(ref);
    };

    // The shared overflow menus (rail rows AND object views render these —
    // the same actions in the same order everywhere). Exports lead with
    // their kind-specific re-download; every kind gets rename + delete.
    // `archived` tells the menu which archive verb to show: an archived row
    // (from the Library "Archived" bucket) offers Unarchive; every other row
    // offers Archive. F86. Distinct commands per intent — never one toggle.
    const resourceOverflowItems = (
        ref: ResourceRef,
        archived?: boolean,
    ): readonly MenuItem[] => [
        ...(ref.type === "export"
            ? [
                  {
                      _tag: "item",
                      label: t`Download again`,
                      onSelect: () => downloadExportAgain(ref),
                  } satisfies MenuItem,
              ]
            : []),
        {
            _tag: "item",
            label: t`Rename`,
            onSelect: () => beginRenameResource(ref),
        },
        // The per-resource door into the History surface, drilled to this
        // resource's version history (right-dock/history-inspection).
        {
            _tag: "item",
            label: t`Version history`,
            onSelect: () => openHistoryForResource(ref.id),
        },
        // Duplicate is material-only: the domain refuses a copy of any other
        // kind (`resource_kind_not_editable`), so the affordance appears only
        // where it can succeed.
        ...(ref.type === "material"
            ? [
                  {
                      _tag: "item",
                      label: t`Duplicate`,
                      onSelect: () => duplicateResourceNow(ref),
                  } satisfies MenuItem,
              ]
            : []),
        // Archive / unarchive (F86) — set aside a live row, or bring an
        // archived one back. The verb shown follows the row's current state.
        archived === true
            ? {
                  _tag: "item",
                  label: t`Unarchive`,
                  onSelect: () => unarchiveResourceNow(ref),
              }
            : {
                  _tag: "item",
                  label: t`Archive`,
                  onSelect: () => archiveResourceNow(ref),
              },
        {
            _tag: "item",
            label: t`Delete…`,
            tone: "danger",
            onSelect: () => setConfirmDelete(ref),
        },
    ];

    // Start a real export: `packageMaterial` (lands a package Resource, yields a
    // `/exports/:id` URL), then move the flow to `done` holding that URL for the
    // Download press. A refusal (unsupported target for this template, material
    // not authored) becomes the `failed` state with a localized reason.
    // Actually build + deliver the package for `formatId` (the packaging leg,
    // shared by the clean path and the review alarm's "Package anyway").
    const packageNow = (formatId: string) => {
        if (exportMaterialId === null) return;
        const materialId = exportMaterialId;
        setExportState({ type: "packaging" });
        const name = `${refFor(materialId)?.title || materialId}.${extForFormat[formatId] ?? "zip"}`;
        const run = async () => {
            const r = await ports.exportMaterial(materialId, formatId);
            Result.match<ExportReady, ExternalError, void>({
                ok: (ready) => {
                    setExportReady(ready);
                    setExportState({ type: "done", downloadName: name });
                },
                err: (e) => {
                    const d = toDisplay(e);
                    // A refusal naming parts (empty, unfinished) lists them
                    // after what to do.
                    const parts = e._tag === "External.Refused" ? e.parts : [];
                    const fix = [
                        ...(d.body === undefined ? [] : [t(d.body)]),
                        ...(parts.length === 0 ? [] : [parts.join(", ")]),
                    ].join(" ");
                    setExportState({
                        type: "failed",
                        message: t(d.title),
                        fix: fix.length === 0 ? undefined : fix,
                        errorId: externalErrorId(e),
                        next: exportNextStep(d.recovery),
                        format: formatId,
                    });
                },
            })(r);
            // The export landed a package Resource in the workspace —
            // re-read the library so the rail's Paket section shows it
            // without a page reload.
            if (Result.isOk(r)) await reloadLibrary();
        };
        void run();
    };

    // The citation-load-bearing gate (.claude/analysis/citation-load-bearing-spec.md):
    // export is the irreversible "leaving the system" moment, so a factual
    // signal becomes load-bearing HERE. MVP is warn-and-override, never a hard
    // block — a loud interstitial `review` step the author can always click
    // through. Honest-signal rule (§3): the alarm reads a material none of
    // whose factual items a knowledge-bank claim backs (the near-zero floor of
    // claim support). Reuses the already-loaded `quality`/`policy` reads for
    // the active material (no new port round-trip); `policy` is the advisory
    // write-boundary evaluation, whose register line reads "you can still
    // package this" — exactly the MVP posture. `unsupportedTerms` stays empty.
    const onExportStart = (formatId: string) => {
        if (exportMaterialId === null) return;
        // The quality reads belong to the edited material; a material exported
        // from elsewhere (a Studio card) has none loaded, so it goes straight
        // to packaging.
        const grounding =
            exportMaterialId === activeMaterialId
                ? quality?.grounding
                : undefined;
        const nothingCited =
            grounding?.type === "grounded" &&
            grounding.support.length > 0 &&
            grounding.support.every((k) => k.cited === 0);
        // Only interrupt when there is something honest to review AND we have a
        // policy evaluation to render the verdict from; otherwise package straight
        // through (a clean material, or an unmeasured one, never stalls).
        if (nothingCited && policy !== null) {
            setExportState({
                type: "review",
                evaluation: policy,
                unsupportedTerms: [],
                format: formatId,
            });
            return;
        }
        packageNow(formatId);
    };

    return {
        submitResourceRename,
        conversationTitleFor,
        submitConversationRename,
        deleteResourceNow,
        undoDelete,
        archiveResourceNow,
        unarchiveResourceNow,
        promoteFileNow,
        deleteConversationNow,
        undoDeleteConversation,
        saveSourceSummaryNow,
        addSourceNote,
        deleteSourceNote,
        requestUploadForSource,
        downloadExportById,
        resourceOverflowItems,
        beginRenameConversation,
        archiveConversationNow,
        unarchiveConversationNow,
        retrySave,
        packageNow,
        onExportStart,
    };
};
